import { useEffect } from 'react';
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { Toaster } from 'sonner';
import { reportClientError } from '@siliconx/shared/telemetry/report-client-error';
import { AppShell } from '../components/shell/AppShell';
import { VerificationGateProvider } from '../components/exchange/VerificationGateProvider';
import { isExchangePath, orgFeatureForPath } from '../components/shell/nav/customerNav';
import { AuthProvider, useAuth } from '../lib/authContext';
import { orgFeatureStateOf } from '../lib/orgFeatures';
import { client, getIdToken, stashReturnTo } from '../lib/auth';
import { API_BASE } from '../lib/api';
import { onboardingPhase } from '../lib/onboardingPhase';
import { AddAssetWizardRailProvider } from '../lib/addAssetWizardRail';

// SIL-1305: re-exported so every existing importer of the redirect-ordering
// gates from this file (tos-gate-redirect.test.tsx, etc.) can pull
// `onboardingPhase` from the same place — the actual implementation lives in
// lib/onboardingPhase.ts (no React import) so AppShell.tsx can import it
// too without a circular dependency back through this file.
export { onboardingPhase, type OnboardingPhase } from '../lib/onboardingPhase';

/**
 * SIL-993/SIL-1132 — first sign-in of an individual user opens the Add Asset
 * wizard, NOT profile completion. An incomplete individual_profile still
 * blocks BOTH buying and selling (approved design: unlock together, one
 * step), but the redirect that used to fire on every route from the moment
 * of first sign-in now only fires once the org already owns an asset
 * (`hasAssets`, sourced from GET /api/client/me — SIL-1132). Before that,
 * `AddAssetGate` (below) routes the user to /assets/submit instead — SIL-1132
 * acceptance criteria: profile completion is required only (a) when the
 * sell flow is attempted (gated independently, at the component level, by
 * `canSell` — e.g. ProfileLockedCard/listings.tsx, unaffected by this file)
 * and (b) once the org's first asset exists. A business org
 * (organizationType !== 'individual') is never redirected by either gate —
 * its existing dual-profile completeness rules are unchanged.
 *
 * Lives inside AuthProvider (useAuth needs the identity query context) but
 * outside AppShell's own concerns — SIL-994 owns portal nav/labels/hiding
 * Team; this gate is scoped to the redirect only, not nav.
 *
 * Must defer to TosGate (below) until `tosAccepted` — otherwise this gate,
 * declared second, wins the redirect race and sends the user to
 * /individual-profile before they've accepted terms; that route isn't
 * ToS-allowlisted, so the API correctly 403s it (tos_required). Exported
 * for the redirect-ordering regression test alongside TosGate's.
 */
/**
 * SIL-1008 — customer-portal ToS clickwrap gate. Runs BEFORE
 * IndividualProfileGate (a user accepts terms before the app asks for
 * profile data, per the design spec) and redirects to /tos from every other
 * route until `tosAccepted` is true. `tosAccepted` is sourced from
 * GET /api/client/me (clientAuth.ts already enforces the hard API-level
 * block independently — this gate is the SPA-side UX counterpart, same
 * relationship as IndividualProfileGate to its own server-side check).
 * Exported for the redirect-behavior test (SIL-1008 acceptance criteria).
 */
export function TosGate() {
  const { isLoading, tosAccepted } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const href = useRouterState({ select: (s) => s.location.href });
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;
    if (tosAccepted) return;
    if (pathname === '/tos') return;
    // SIL-1187: stash the originally-requested destination (same
    // same-origin-only validated `location.href` shape stashReturnTo already
    // uses for the Cognito round-trip above) so accepting the clickwrap can
    // send the user back instead of stranding them on /tos.
    stashReturnTo(href);
    navigate({ to: '/tos', replace: true });
  }, [isLoading, tosAccepted, pathname, href, navigate]);

  return null;
}

/**
 * Pure predicate backing `IndividualProfileGate` below — exported so
 * `OrgFeatureGate` can defer to it without duplicating the condition (same
 * redirect-ordering hazard PR #1691 fixed for TosGate/IndividualProfileGate:
 * two gates independently deciding "should I redirect" without sharing logic
 * is how that race bug happened the first time).
 */
/**
 * SIL-1305: derives from `onboardingPhase` instead of re-deriving the
 * tosAccepted/organizationType/canSell/canBuy/hasAssets condition a third
 * time — this predicate now only owns the pathname exemptions layered on
 * top of "is the user in the 'profile' phase". See onboardingPhase's own
 * docstring (lib/onboardingPhase.ts) for the phase rules themselves.
 */
export function individualProfileGateWouldRedirect(
  auth: Pick<
    ReturnType<typeof useAuth>,
    'isLoading' | 'tosAccepted' | 'organizationType' | 'canSell' | 'canBuy' | 'hasAssets'
  >,
  pathname: string,
): boolean {
  if (auth.isLoading) return false;
  if (!auth.tosAccepted) return false;
  if (auth.organizationType !== 'individual') return false;
  if (auth.canSell && auth.canBuy) return false;
  // SIL-1812: profile completion is deferred to the Exchange. Only the buy/sell
  // surfaces (`pillar: 'Exchange'` in CUSTOMER_NAV) are gated; the rest of the
  // portal (assets, dashboard, ...) is reachable with an incomplete profile.
  // The server-side buy/sell gates are unchanged.
  if (!isExchangePath(pathname)) return false;
  // /listings keeps its own in-page ProfileLockedCard rather than bouncing.
  if (pathname === '/listings' || pathname.startsWith('/listings/')) return false;
  // Pre-first-asset the Add Asset gate owns the redirect.
  if (!auth.hasAssets) return false;
  return true;
}

/**
 * SIL-1132 — pure predicate backing `AddAssetGate` below, same shape as
 * `individualProfileGateWouldRedirect`: an individual org with an
 * incomplete profile and NO assets yet (first sign-in) is routed to the Add
 * Asset wizard (/assets/submit) rather than /individual-profile. Once the
 * org's first asset exists, this predicate stops firing and
 * `individualProfileGateWouldRedirect` takes over — the two are mutually
 * exclusive by construction (both gate on `!(canSell && canBuy)`, but this
 * one requires `!hasAssets` and the other requires `hasAssets`).
 */
/**
 * SIL-1305: derives from `onboardingPhase` instead of re-deriving the
 * condition a third time — see individualProfileGateWouldRedirect's
 * docstring above for the shared rationale.
 */
export function addAssetGateWouldRedirect(
  auth: Pick<
    ReturnType<typeof useAuth>,
    'isLoading' | 'tosAccepted' | 'organizationType' | 'canSell' | 'canBuy' | 'hasAssets'
  >,
  pathname: string,
): boolean {
  if (auth.isLoading) return false;
  if (onboardingPhase(auth) !== 'add-asset') return false;
  if (pathname === '/assets/submit') return false;
  // A user who navigates to /individual-profile voluntarily (e.g. to unlock
  // selling before adding any assets) is never bounced back to the wizard —
  // this gate only forces the wizard as the DEFAULT first-login landing, it
  // never blocks the profile-completion route itself.
  if (pathname === '/individual-profile') return false;
  // Bench pairing: a technician scans the bench QR on a phone, often before
  // the org has any assets. Redirecting would drop the ?code= and the device
  // would time out waiting for approval. Segment-boundary match only.
  if (pathname === '/bench' || pathname.startsWith('/bench/')) return false;
  return true;
}

export function AddAssetGate() {
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets } = auth;

  useEffect(() => {
    if (addAssetGateWouldRedirect(auth, pathname)) {
      navigate({ to: '/assets/submit', replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps mirror addAssetGateWouldRedirect's own inputs
  }, [isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets, pathname, navigate]);

  return null;
}

export function IndividualProfileGate() {
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets } = auth;

  useEffect(() => {
    if (individualProfileGateWouldRedirect(auth, pathname)) {
      // SIL-1812: `return` brings the user back once the profile is complete.
      navigate({ to: '/individual-profile', search: { return: pathname }, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps mirror individualProfileGateWouldRedirect's own inputs
  }, [isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets, pathname, navigate]);

  return null;
}

/**
 * SIL-1019/SIL-1034 — root-level backstop for `NavEntry.orgFeature`
 * (customerNav.ts): the nav filter already hides a gated item (e.g. Team)
 * for an org type that doesn't unlock it, but a direct URL hit bypasses nav
 * entirely, so this gate blocks that too. Single source of truth is
 * `orgFeatureForPath` (customerNav.ts), built from `CUSTOMER_NAV` itself
 * plus the non-nav gated routes (Finance setup/triage, onboarding) — this
 * gate does not duplicate route names.
 *
 * Ordering (mirrors TosGate → IndividualProfileGate precedent, PR #1691's
 * gate-race fix): runs LAST of the three root gates. Must defer to TosGate
 * until `tosAccepted` (same reason as IndividualProfileGate — the API 403s
 * any non-allowlisted route pre-ToS anyway, so redirecting to a gated
 * destination first would be a wasted, confusing hop). Must also defer to
 * IndividualProfileGate — shares its exact condition via
 * `individualProfileGateWouldRedirect` rather than re-deriving it, closing
 * the two-independent-conditions hazard the old `BusinessOnlyGate` had (its
 * own canSell/canBuy check could silently drift from IndividualProfileGate's).
 *
 * Redirects only on `'disabled'` (SIL-1034 plan §6.3) — `'pending'`
 * (identity still loading) is deliberately NOT a redirect trigger, unlike
 * the old gate's `isLoading` early-return which had the same effect via a
 * different mechanism; `orgFeatureStateOf` makes that "don't redirect while
 * unresolved" semantics explicit and shared with nav hiding
 * (`useOrgFeatureState`) instead of duplicated.
 *
 * Navigates to `/dashboard` (not `/`, SIL-1034 plan §7 item 3) — `/`
 * redirects to `/dashboard` via routes/index.tsx anyway, so this is the same
 * destination with one fewer hop.
 *
 * SIL-1132: also defers to `AddAssetGate` (same sharing rationale as
 * IndividualProfileGate above) — moot for `/assets/submit` itself today
 * (it isn't `orgFeature`-gated), but keeps this gate's precondition list
 * exhaustive rather than silently correct by omission.
 */
export function OrgFeatureGate() {
  const auth = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const { isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets } = auth;

  useEffect(() => {
    if (isLoading) return;
    if (!tosAccepted) return; // defer to TosGate
    if (addAssetGateWouldRedirect(auth, pathname)) return; // defer to AddAssetGate
    if (individualProfileGateWouldRedirect(auth, pathname)) return; // defer to IndividualProfileGate

    const feature = orgFeatureForPath(pathname);
    if (feature === null) return;
    if (orgFeatureStateOf({ isLoading, organizationType }, feature) !== 'disabled') return;

    navigate({ to: '/dashboard', replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps mirror this effect's own reads of `auth`
  }, [isLoading, tosAccepted, organizationType, canSell, canBuy, hasAssets, pathname, navigate]);

  return null;
}

/**
 * SIL-666: route-level failure fallback (loader/component throws during
 * navigation) — the counterpart to main.tsx's root ErrorBoundary, which
 * only catches errors during React's own render/commit, not errors thrown
 * from a route's `loader`/`beforeLoad`. Reports once per distinct error via
 * the effect's dependency array, not on every re-render.
 *
 * 2026-09-20 live-site audit finding: a signed-out visitor who lands on an
 * authenticated surface used to see this generic "failed to load" card with
 * no indication the real cause was "you're not signed in" — the raw
 * `Unauthorized` string only appeared in the (uncollapsed) React error
 * detail, not as guidance. When the thrown error is that exact 401 body,
 * show a plain-language sign-in prompt instead of the generic message; any
 * other error keeps the original copy.
 */
function RouteErrorFallback({ error }: ErrorComponentProps) {
  useEffect(() => {
    reportClientError(API_BASE, {
      message: error.message || String(error),
      name: error.name,
      stack: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      portal: 'customer',
      source: 'router-error',
    }, getIdToken);
  }, [error]);

  const isUnauthorized = error.message?.toLowerCase() === 'unauthorized';

  if (isUnauthorized) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-lg font-semibold text-foreground">You need to sign in to view this page.</h1>
        <a
          href="/dashboard"
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Sign in
        </a>
      </div>
    );
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-lg font-semibold text-foreground">This page failed to load</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        An unexpected error occurred and the team has been notified. Try reloading the page.
      </p>
      <button
        type="button"
        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        onClick={() => window.location.reload()}
      >
        Reload page
      </button>
    </div>
  );
}

/**
 * Root route — wraps every child in AuthProvider + AppShell.
 * beforeLoad enforces Cognito auth on every route except /auth/callback.
 * The onboarding wizard (/onboarding) is optional and is never gated here —
 * see dashboard.tsx for its entry point.
 * Set VITE_DEV_SKIP_AUTH=true to bypass during local development.
 *
 * Follow-up to #1653: /auth/callback must render standalone, without
 * AuthProvider or AppShell. Both mount unguarded queries on load (client
 * identity fetch in AuthProvider, unread-count in AppShell's NotificationBell)
 * that 401 before exchangeCodeForTokens finishes writing tokens — the shared
 * QueryCache.onError then treats that 401 as a real session expiry and
 * triggers another Cognito redirect, aborting the in-flight token exchange
 * and looping. beforeLoad already skips the auth check for this path; the
 * component must skip the shell for the same reason.
 */
/**
 * SIL-1050: /signup must render for a SIGNED-OUT browser, no Hosted UI
 * redirect — the whole point of a referral-link signup is that the visitor
 * has no Cognito session yet. Same "render standalone, skip AuthProvider/
 * AppShell" shape as /auth/callback above, and skipped by beforeLoad's own
 * `client.silentRefreshOrRedirect()` call below for the identical reason:
 * both AuthProvider's identity query and AppShell's notification query 401
 * for an anonymous caller and would otherwise trigger a real redirect.
 * `#code=...` is a URL FRAGMENT, never sent to the server and never part of
 * TanStack Router's `pathname` — this check only ever needs the path.
 */
export function isPublicSignupPath(pathname: string): boolean {
  return pathname === '/signup' || pathname.startsWith('/signup/');
}

/**
 * 2026-09-20 live-site audit finding: /tos and /privacy used to render
 * inside the SAME authenticated shell as every other route — a signed-out
 * visitor following either link (from /signup's footer, an email footer, or
 * a bookmark) hit beforeLoad's Cognito redirect or, worse, AuthProvider's
 * unguarded GET /api/client/me 401ing straight into RouteErrorFallback's
 * generic error card, never seeing the actual Terms/Privacy content.
 * /signup already proved there's no global auth gate, so both join it here:
 * no portal chrome (AppShell header/bell/sign-out), no client/me query, same
 * paths as before (linked from the signup form, footer, and possibly stored
 * consent/emails — never renamed). An authenticated user hitting either
 * route (e.g. TosGate's own redirect, below) still gets the exact same
 * component tree; nothing here touches the session/tokens, so there's no
 * session loss or bounce back to a logged-out shell either way. See
 * routes/tos.tsx's own docstring for how that route's component handles
 * both the signed-in (clickwrap acceptance) and signed-out (read-only)
 * cases now that its route no longer implies a session.
 */
export function isPublicPortalPath(pathname: string): boolean {
  return isPublicSignupPath(pathname) || pathname === '/tos' || pathname === '/privacy' || pathname === '/terms';
}

export function RootComponent() {
  // BUG-01 (2026-09-21): key the public/authenticated split on the leaf match
  // <Outlet /> is actually RENDERING, not on `s.location`. `location` flips to
  // the destination at navigation start while `matches` still holds the
  // previous route until the new one is ready - so TosGate's /dashboard ->
  // /tos redirect dropped AuthProvider with /dashboard still mounted, and its
  // useOrgFeature() threw "useAuth must be used within <AuthProvider>".
  const pathname = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.pathname ?? s.location.pathname,
  });
  const isAuthCallback = pathname.startsWith('/auth/callback');
  const isPublicPortal = isPublicPortalPath(pathname);

  if (isAuthCallback || isPublicPortal) {
    return (
      <>
        <Outlet />
        <Toaster position="bottom-right" richColors theme="system" />
      </>
    );
  }

  return (
    <AuthProvider>
      <TosGate />
      <AddAssetGate />
      <IndividualProfileGate />
      <OrgFeatureGate />
      {/* SIL-1346: hosts the ONE buyer-verification modal every Exchange
          action gate opens (components/exchange/VerificationGateProvider.tsx).
          Mounted here so a screen only has to call useVerificationGate() -
          the provider itself issues no request, so this costs nothing on a
          page that never gates anything. */}
      <VerificationGateProvider>
        <AddAssetWizardRailProvider>
          <AppShell>
            <Outlet />
          </AppShell>
        </AddAssetWizardRailProvider>
      </VerificationGateProvider>
      <Toaster position="bottom-right" richColors theme="system" />
    </AuthProvider>
  );
}

export const Route = createRootRoute({
  async beforeLoad({ location }) {
    // Skip auth check on the callback route itself.
    if (location.pathname.startsWith('/auth/callback')) return {};
    // SIL-1050: /signup is public — a referral-link visitor has no Cognito
    // session by definition. /tos, /privacy and /terms (SIL-1509) join it (2026-09-20 audit
    // finding) — see isPublicPortalPath's own comment above.
    if (isPublicPortalPath(location.pathname)) return {};

    // Remember the originally-requested destination so the callback page can
    // restore it after a genuine Cognito round-trip — a no-op re-stash on
    // every real route load until it's consumed once post-login.
    stashReturnTo(location.href);

    // Dev bypass.
    if (import.meta.env.VITE_DEV_SKIP_AUTH === 'true') {
      return {};
    }

    // Silently refresh if the session is expired, else redirect to Cognito
    // Hosted UI and block routing indefinitely while the browser navigates away.
    await client.silentRefreshOrRedirect();

    return {};
  },

  component: RootComponent,

  errorComponent: RouteErrorFallback,
});
